import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import authService from "../Services/authService";
import accountUpgradeService from "../Services/accountUpgradeService";
import BottomSheet from "./BottomSheet";

const RoomDashboardSheet = ({ isOpen, onClose, onUpgradeRequest }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountInfo, setAccountInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadUserRooms();
      checkAccountStatus();
    }
  }, [isOpen]);

  const checkAccountStatus = async () => {
    try {
      const info = await accountUpgradeService.getAccountInfo();
      setAccountInfo(info);
    } catch (e) {
      console.error("Error checking account status:", e);
    }
  };

  const loadUserRooms = async () => {
    try {
      setLoading(true);
      const userRooms = await authService.getUserRooms();

      const roomsWithStatus = await Promise.all(
        userRooms.map(async (room) => {
          try {
            const roomQuery = query(collection(db, "rooms"), where("roomId", "==", room.roomId));
            const roomSnapshot = await getDocs(roomQuery);
            if (!roomSnapshot.empty) {
              const data = roomSnapshot.docs[0].data();
              return {
                ...room,
                currentStatus: data.gameStatus || "waiting",
                isActive: true,
                isDeleted: false,
                playerCount: data.players?.length || 0,
                maxPlayers: data.maxPlayers || 3,
                currentRound: data.currentRound || 0,
              };
            }
            return { ...room, currentStatus: "deleted", isActive: false, isDeleted: true, playerCount: 0, maxPlayers: 3, currentRound: 0 };
          } catch {
            return { ...room, currentStatus: "unknown", isActive: false, isDeleted: false, playerCount: 0, maxPlayers: 3, currentRound: 0 };
          }
        })
      );

      roomsWithStatus.sort((a, b) => {
        const getDateValue = (dateValue) => {
          if (!dateValue) return 0;
          if (dateValue && typeof dateValue.toDate === "function") return dateValue.toDate().getTime();
          if (dateValue instanceof Date) return dateValue.getTime();
          if (typeof dateValue === "number") return dateValue;
          return new Date(dateValue).getTime();
        };
        return getDateValue(b.joinedAt) - getDateValue(a.joinedAt);
      });

      setRooms(roomsWithStatus);
    } catch (e) {
      console.error("Error loading user rooms:", e);
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = (roomId) => {
    localStorage.setItem("currentRoomId", roomId);
    navigate("/create-room");
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "waiting": return "bg-blue-100 text-blue-800";
      case "starting": return "bg-yellow-100 text-yellow-800";
      case "playing": return "bg-green-100 text-green-800";
      case "ended": return "bg-gray-100 text-gray-800";
      case "deleted": return " ";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "waiting": return "Waiting";
      case "starting": return "Starting";
      case "playing": return "Playing";
      case "ended": return "Ended";
      case "deleted": return " ";
      default: return "Unknown";
    }
  };

  const formatDate = (dateValue) => {
    try {
      let date;
      if (dateValue && typeof dateValue.toDate === "function") date = dateValue.toDate();
      else if (dateValue instanceof Date) date = dateValue;
      else date = new Date(dateValue);
      if (isNaN(date.getTime())) return "Unknown date";
      return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "Unknown date";
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="My Rooms" subtitle="View your recent rooms">
      {loading && (
        <div className="text-center py-8">
          <div className="text-gray-500">Loading your rooms...</div>
        </div>
      )}

      {!loading && rooms.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-700 font-semibold mb-1">No rooms yet</div>
          <p className="text-sm text-gray-600">Rooms you create or join will show up here.</p>
          <button
            onClick={onClose}
            className="mt-4 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm px-5 py-2.5 rounded-xl"
          >
            Create a room
          </button>
          {accountInfo?.type === "anonymous" && (
            <div className="pt-3">
              <button onClick={onUpgradeRequest} className="min-h-[44px] text-red-600 hover:text-red-700 font-medium text-sm underline">
                Create an account to keep your rooms on any device
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && rooms.length > 0 && (
        <div className="space-y-3">
          {rooms.slice(0, 5).map((room) => (
            <div key={room.roomId} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <h3 className="font-semibold">{room.roomName}</h3>
                  {room.isHost && (
                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-medium">Host</span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(room.currentStatus)}`}>
                    {getStatusText(room.currentStatus)}
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-3">
                <div className="flex items-center space-x-4">
                  {room.currentRound > 0 && <span>Round: {room.currentRound}</span>}
                  <span>Joined: {formatDate(room.joinedAt)}</span>
                </div>
              </div>
              {room.isActive && !room.isDeleted && room.currentStatus !== "ended" && (
                <button
                  onClick={() => joinRoom(room.roomId)}
                  className="w-full bg-red-600 text-white py-2.5 rounded-xl hover:bg-red-700 transition-colors font-medium text-sm"
                >
                  {room.currentStatus === "waiting" ? "Rejoin Room" : "Continue Game"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </BottomSheet>
  );
};

export default RoomDashboardSheet;
